import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { BrandWordmark, OrbMark } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [needsSetup, setNeedsSetup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState({ first_name: "", last_name: "", email: "", password: "", pin: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(apiError(err, "Could not send reset email"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/auth/bootstrap").then((r) => setNeedsSetup(!!r.data.needs_setup)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "staff" ? "/my" : "/");
    } catch (err) {
      setError(apiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", setup);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err, "Setup failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 pt-safe">
      <div className="hidden md:flex relative flex-col items-center justify-center text-white p-12"
        style={{ background: "radial-gradient(42rem 42rem at 50% 34%, rgba(124,58,237,0.4), transparent 65%), linear-gradient(to bottom, #1B1038 0%, #07050A 72%)" }}>
        <OrbMark size={190} animated className="mb-10" />
        <h1 className="font-heading text-5xl font-bold leading-tight">MISE</h1>
        <p className="mt-3 text-sm uppercase tracking-[0.3em] text-[#C9A8FF]" data-testid="login-tagline">Prepared Intelligence</p>
        <p className="mt-5 text-white/60 max-w-sm text-center text-lg">
          Every moving part of your restaurant, run by one intelligence.
        </p>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        {needsSetup ? (
          <form onSubmit={submitSetup} className="w-full max-w-sm space-y-5" data-testid="setup-form">
            <div>
              <BrandWordmark size={32} dark={false} sub />
              <h2 className="font-heading text-2xl font-semibold hidden md:block">First-run setup</h2>
              <p className="text-sm text-muted-foreground mt-1">Create the owner account to get started</p>
            </div>
            {error && (
              <div data-testid="setup-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="setup-first">First name</Label>
                <Input id="setup-first" data-testid="setup-first-name" required value={setup.first_name}
                  onChange={(e) => setSetup({ ...setup, first_name: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="setup-last">Last name</Label>
                <Input id="setup-last" data-testid="setup-last-name" required value={setup.last_name}
                  onChange={(e) => setSetup({ ...setup, last_name: e.target.value })} className="h-11" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="setup-email">Email</Label>
              <Input id="setup-email" data-testid="setup-email" type="email" required value={setup.email}
                onChange={(e) => setSetup({ ...setup, email: e.target.value })} placeholder="owner@restaurant.co.za" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="setup-password">Password (min 8 characters)</Label>
              <Input id="setup-password" data-testid="setup-password" type="password" required minLength={8} value={setup.password}
                onChange={(e) => setSetup({ ...setup, password: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="setup-pin">MISE Shift PIN (4 digits)</Label>
              <Input id="setup-pin" data-testid="setup-pin" inputMode="numeric" maxLength={4} required value={setup.pin}
                onChange={(e) => setSetup({ ...setup, pin: e.target.value.replace(/\D/g, "") })} placeholder="••••" className="h-11" />
            </div>
            <Button data-testid="setup-submit" type="submit" disabled={loading}
              className="w-full h-11 bg-primary hover:bg-[#8B5CF6] text-white transition-colors">
              {loading ? "Creating…" : "Create owner account"}
            </Button>
          </form>
        ) : forgot ? (
          <form onSubmit={submitForgot} className="w-full max-w-sm space-y-6" data-testid="forgot-form">
            <div>
              <BrandWordmark size={32} dark={false} sub />
              <h2 className="font-heading text-2xl font-semibold hidden md:block">Reset your password</h2>
              <p className="text-sm text-muted-foreground mt-1">We'll email you a one-time reset link (valid 30 minutes).</p>
            </div>
            {error && (
              <div data-testid="forgot-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            {forgotSent ? (
              <div className="space-y-4">
                <div data-testid="forgot-success" className="text-sm text-[#A9C97E] bg-[#4A5D23]/15 border border-[#4A5D23]/40 rounded-md px-3 py-2">
                  If an account exists for that email, a reset link is on its way.
                </div>
                <Button type="button" variant="ghost" data-testid="forgot-back" className="w-full"
                  onClick={() => { setForgot(false); setForgotSent(false); setError(""); }}>
                  Back to sign in
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input id="forgot-email" data-testid="forgot-email" type="email" required value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@restaurant.co.za" className="h-11" />
                </div>
                <Button data-testid="forgot-submit" type="submit" disabled={loading}
                  className="w-full h-11 bg-primary hover:bg-[#8B5CF6] text-white transition-colors">
                  {loading ? "Sending…" : "Email me a reset link"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Remembered it? <button type="button" className="text-primary underline" data-testid="forgot-cancel"
                    onClick={() => { setForgot(false); setError(""); }}>Back to sign in</button>
                </p>
              </>
            )}
          </form>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
            <div>
              <BrandWordmark size={32} dark={false} sub />
              <h2 className="font-heading text-2xl font-semibold hidden md:block">Sign in</h2>
              <p className="text-sm text-muted-foreground mt-1">Owner, manager and staff access</p>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@restaurant.co.za" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-11" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-11 bg-primary hover:bg-[#8B5CF6] text-white transition-colors">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              <button type="button" className="text-primary underline" data-testid="login-forgot-link"
                onClick={() => { setForgot(true); setError(""); setForgotEmail(email); }}>Forgot your password?</button>
            </p>
            <p className="text-xs text-muted-foreground text-center">
              Staff clocking in? Use the <a href="/kiosk" className="text-primary underline" data-testid="login-kiosk-link">MISE Shift screen</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
